
import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import { t } from '../locales';
import { Language } from '../types';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  isDestructive?: boolean;
  language: Language;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    title, 
    message, 
    confirmLabel, 
    cancelLabel, 
    isDestructive = true,
    language
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="bg-white dark:bg-gray-800 w-full max-w-sm rounded-2xl shadow-2xl p-6 relative z-10 animate-popIn border border-gray-100 dark:border-gray-700">
        
        <div className="flex items-start gap-4 mb-4">
            <div className={`p-3 rounded-full flex-shrink-0 ${isDestructive ? 'bg-red-50 dark:bg-red-900/20 text-red-500' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-500'}`}>
                <AlertTriangle size={24} />
            </div>
            <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white leading-tight">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                    {message}
                </p>
            </div>
        </div>

        <div className="flex gap-3">
            <button 
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
            >
                {cancelLabel || t(language, 'cancel')}
            </button>
            <button 
                onClick={() => { onConfirm(); onClose(); }}
                className={`flex-1 py-2.5 rounded-xl font-bold text-white shadow-lg transition-transform active:scale-95 text-sm ${isDestructive ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}
            >
                {confirmLabel || t(language, 'deleteItem')}
            </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmationModal;
